// P2-9: conservative, deterministic removal of non-manuscript chrome from
// extracted PDF text.
//
// Two artefacts corrupt the deterministic pipeline:
//   1. Running heads / footers / page numbers repeated on every page (journal
//      name, "Page 3 of 12", DOI/copyright strap-lines).
//   2. Line-number gutters — including manuscripts submitted ONLY as a
//      line-numbered copy, where the Editorial-Manager duplicate-collapse has
//      no clean twin to keep and the numbers stay embedded in the body.
//
// Prime directive (matches the rest of this engine): mangling real content —
// broken references, dropped sentences, lost numbers — is far worse than
// leaving some chrome behind. Every rule here is high-precision and
// SELF-DISABLING: a normal PDF with no repeats and no gutter passes through
// byte-for-byte unchanged, and a hard safety-net reverts to the original text
// if cleanup ever removes too much.

const SECTION_HEADING_RE =
  /^(abstract|summary|introduction|background|methods?|materials?\s+and\s+methods|patients?\s+and\s+methods|methodology|results?|discussion|conclusions?|references?|bibliography|works\s+cited|acknowledge?ments?|funding|appendix|supplementary)\s*:?\s*$/i;

/** Normalise a line for repeat-detection: case-fold, collapse ws, mask digits
 *  so "Page 3 of 12" / "Page 4 of 12" collapse to one templated key. */
function chromeKey(line: string): string {
  return line
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/\d+/g, '#');
}

// Hard page-furniture cues. A repeated short line is only treated as a
// running head/footer if it carries one of these — journal/page/DOI/URL/
// copyright/volume markers. This is deliberately strict: real-PDF evidence
// showed a "≤N words" shape rule also deletes recurring TABLE CELL values
// ("cohort", "echocardiography", "prospective") in characteristics tables —
// destroying real content. Requiring a cue removes genuine furniture while
// leaving content (cell values, numeric fragments) untouched. Pre-print EM
// submissions have no publisher furniture, so cleanup becomes a safe no-op.
const CHROME_CUE_RE =
  /(\bpages?\b|\bpp?\.|\bvol\.?\b|\bvolume\b|\bissue\b|\bno\.\s*\d|\bdoi\b|https?:|www\.|\bissn\b|\bisbn\b|copyright|©|all rights reserved|downloaded from|\bjournal\b)/i;

/**
 * A line is "chrome-shaped" — eligible to be treated as a repeated running
 * head/footer — only if it is short, not a section heading, AND carries an
 * explicit furniture cue. A repeated content line (sentence, table cell,
 * numeric fragment) carries no cue and is therefore NEVER removed.
 */
function chromeShaped(t: string): boolean {
  if (t.length === 0 || t.length > 70) return false;
  if (SECTION_HEADING_RE.test(t)) return false;
  return CHROME_CUE_RE.test(t);
}

export interface CleanupResult {
  text: string;
  /** Count of repeated head/footer/page-number lines removed. */
  removedRepeatedLines: number;
  /** True iff a line-number gutter was confidently detected and stripped. */
  strippedGutter: boolean;
}

// A leading gutter token: a bare 1–4 digit integer at the very start of the
// line, NOT immediately followed by a list/enumerator/ratio/decimal/range
// marker ("1." "1)" "1:" "12,345" "1-2" "1%"). Group 1 = leading whitespace,
// 2 = the integer, 3 = the separating whitespace or end-of-line.
const LEAD_GUTTER_RE = /^(\s*)(\d{1,4})(?![-.):%\d,])(\s+|$)/;
const LONE_INT_RE = /^\s*\d{1,4}\s*$/;

export function cleanupExtractedText(input: string): CleanupResult {
  const lines = input.split('\n');

  // ---- Pass 1: repeated running head / footer / page-number lines ----
  const freq = new Map<string, number>();
  for (const line of lines) {
    const t = line.trim();
    if (!chromeShaped(t)) continue;
    const key = chromeKey(t);
    // Skip lines that normalise to nothing meaningful (pure punctuation).
    if (!/[a-z#]/.test(key)) continue;
    freq.set(key, (freq.get(key) ?? 0) + 1);
  }
  // ≥3 verbatim (digit-masked) occurrences of a short non-heading line is a
  // running head/footer: genuine body prose effectively never repeats this way.
  const REPEAT_MIN = 3;
  const chrome = new Set<string>();
  for (const [k, n] of freq) if (n >= REPEAT_MIN) chrome.add(k);

  let removedRepeatedLines = 0;
  const afterPass1: string[] = [];
  for (const line of lines) {
    const t = line.trim();
    if (chromeShaped(t) && chrome.has(chromeKey(t))) {
      removedRepeatedLines++;
      continue;
    }
    afterPass1.push(line);
  }

  // ---- Pass 2: line-number gutter (only when confidently a gutter) ----
  const nonEmpty = afterPass1.filter((l) => l.trim().length > 0);
  const leadInts: number[] = [];
  let candidateLines = 0;
  for (const l of nonEmpty) {
    const t = l.trim();
    const m = LEAD_GUTTER_RE.exec(l);
    if (LONE_INT_RE.test(t)) {
      candidateLines++;
      leadInts.push(parseInt(t, 10));
    } else if (m) {
      candidateLines++;
      leadInts.push(parseInt(m[2], 10));
    }
  }

  let strippedGutter = false;
  if (
    candidateLines >= 25 &&
    nonEmpty.length > 0 &&
    candidateLines >= nonEmpty.length * 0.45 &&
    leadInts.length >= 25
  ) {
    let ascents = 0;
    const deltaCount = new Map<number, number>();
    for (let i = 1; i < leadInts.length; i++) {
      if (leadInts[i] >= leadInts[i - 1]) ascents++;
      const d = leadInts[i] - leadInts[i - 1];
      if (d > 0) deltaCount.set(d, (deltaCount.get(d) ?? 0) + 1);
    }
    const mono = ascents >= (leadInts.length - 1) * 0.85;
    let modalDelta = 0;
    let modalN = 0;
    for (const [d, n] of deltaCount) {
      if (n > modalN) {
        modalN = n;
        modalDelta = d;
      }
    }
    const plausibleStep = [1, 2, 5, 10].includes(modalDelta);
    const span = Math.max(...leadInts) - Math.min(...leadInts);
    if (mono && plausibleStep && span >= 25) strippedGutter = true;
  }

  let out: string[];
  if (strippedGutter) {
    out = [];
    for (const line of afterPass1) {
      if (LONE_INT_RE.test(line)) continue; // lone gutter tick → drop the line
      out.push(line.replace(LEAD_GUTTER_RE, '$1'));
    }
  } else {
    out = afterPass1;
  }

  const text = out.join('\n');
  // Hard safety net: never hand back a gutted body. If cleanup removed more
  // than half the non-whitespace content something went wrong — a noisy
  // analysis beats an empty one.
  const before = input.replace(/\s+/g, '').length;
  const after = text.replace(/\s+/g, '').length;
  if (before > 0 && after < before * 0.5) {
    return { text: input, removedRepeatedLines: 0, strippedGutter: false };
  }
  return { text, removedRepeatedLines, strippedGutter };
}
